import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CapabilitiesService } from '@tenant/capabilities';
import { ApiConfig } from '@tenant/helpers';
import { NgxPermissionsService } from 'ngx-permissions';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Profile, hasOversightRole } from './profile.model';
import { SessionService } from './session.service';

@Injectable()
export class ProfileService {
  private _state = new BehaviorSubject<Profile>(null);

  public get state(): Observable<Profile | null> {
    return this._state.asObservable();
  }

  public get userId() {
    return this._state.getValue().id;
  }

  private userObj: Profile = null;

  constructor(
    private api: HttpClient,
    private _session: SessionService,
    private perm: NgxPermissionsService,
    private capabilities: CapabilitiesService
  ) {
    const usrTmp = JSON.parse(localStorage.getItem('user'));
    if (usrTmp) {
      this.userObj = new Profile(usrTmp);
      this._state.next(this.userObj);
    }
    this.initRoles();
    _session.state.subscribe((loggedIn) => {
      if (!loggedIn) {
        return;
      }
      this.loadData().subscribe(() => {
        return '';
      });
    });
    this.state.subscribe((v) => {
      // Keep the capability layer fed with the current profile. This fires
      // synchronously here (BehaviorSubject) and on every subsequent profile
      // change, so caps.check(...) inside the ngx-permission callbacks below is
      // always evaluated against the latest profile.
      this.capabilities.setProfile(v);
      if (v) {
        localStorage.setItem('user', JSON.stringify(v));
        this.userObj = v;
      }
    });
  }

  public forgetProfile() {
    localStorage.removeItem('user');
    this._state.next(null);
  }

  public loadData() {
    return this.api.get(ApiConfig.currentUserInfo).pipe(
      map((res) => {
        const json = new Profile(res);
        this._state.next(json);
        this.initRoles();
        return json;
      })
    );
  }

  public getUserDetails() {
    return this.api.get(ApiConfig.currentUserInfo).pipe(
      map((res) => {
        const json = new Profile(res);
        return json;
      })
    );
  }

  public update(data) {
    return this.api.patch(ApiConfig.userInfo, { user: data }).pipe(
      map((res) => {
        const json = new Profile(res);
        this._state.next(json);
        return json;
      })
    );
  }

  private initRoles(): void {
    const caps = this.capabilities;
    const roles = () => this._state.value?.roles;
    const permissions = () => this._state.value?.permissions ?? [];

    // ───────────────────────────────────────────────────────────────────────
    // Re-pointed to the capability layer. The backend already returns canonical
    // role flags (`ProfileType`) + permission CODES, so each capability resolves
    // from the REAL payload and exactly mirrors the backend's own access rule
    // (which it also enforces with a 403). This REPLACES the previous gates that
    // read `roles.is_ot_admin` — a field the backend no longer sends. Canonical
    // correctness is asserted in
    // libs/capabilities/src/lib/initroles-repoint.spec.ts.
    // ───────────────────────────────────────────────────────────────────────
    // Deal / approval / accounting / commission.
    this.perm.addPermission('accounting', () => caps.check('canViewDealAccounting'));
    // approve = canApproveDeal (admin/teamlead/office_manager + application_processing);
    // reject = canRejectDeal (same leadership tier + deal_management — split out of
    // the approvals-queue row so view and write stay separate capabilities).
    this.perm.addPermission('deal_manage_approve', () => caps.check('canApproveDeal'));
    this.perm.addPermission('deal_manage_reject', () => caps.check('canRejectDeal'));
    this.perm.addPermission(
      ['deal_change_commission_status', 'deal_change_agent_paid_status'],
      () => caps.check('canManageAgentCommissionPayments')
    );

    // Deal-list filters / work queue.
    this.perm.addPermission('filter_team', () => caps.check('canFilterDealsByTeam'));
    this.perm.addPermission('filter_agents', () => caps.check('canFilterDealsByAgent'));
    // Office filter is CA/OM only (canFilterDealsByOffice deliberately omits
    // teamlead — a teamlead's scope is a single team within one office).
    this.perm.addPermission('filter_office', () =>
      caps.check('canFilterDealsByOffice')
    );
    // Deals-list "All States" filter — OM ONLY per PO decision on FGR-5893
    // (admins don't get it, unlike filter_office); the UI additionally hides it
    // when the requester has a single visible state.
    this.perm.addPermission('canFilterDealsByState', () =>
      caps.check('canFilterDealsByState')
    );
    this.perm.addPermission('work_queue', () => caps.check('canViewDealApprovalsQueue'));
    this.perm.addPermission('work_queue_reports', () =>
      caps.check('canViewWorkQueueDashboard')
    );

    // Applications.
    this.perm.addPermission('applications_reports', () =>
      caps.check('canManageRentalApplications')
    );
    this.perm.addPermission('tenant_reports', () => caps.check('canManageTenantReport'));
    // view_reports gates tenant-report ORDERING UI ("Order Tenant Reports" /
    // "Go To Tenant Reports"), so it maps to canManageTenantReport
    // (admin/teamlead/office_manager + application_processing) — NOT the any-staff
    // canViewCompanyReportsHub, which would widen an action surface to every
    // staff/agent.
    this.perm.addPermission('view_reports', () => caps.check('canManageTenantReport'));

    // Reports. NOTE: this TIGHTENS the report routes — they were visible to every
    // logged-in user (a known ot-looser leak; the backend 403s the data anyway).
    // They now require deal_management + an eligible role, mirroring the backend.
    this.perm.addPermission('reports_team_rankings', () =>
      caps.check('canViewMyTeamRankingsReport')
    );
    this.perm.addPermission('reports_production_detail', () =>
      caps.check('canViewProductionDetailReport')
    );
    this.perm.addPermission('reports_payment_history', () =>
      caps.check('canViewPaymentHistoryReport')
    );
    this.perm.addPermission('reports_lead_sources', () =>
      caps.check('canViewLeadSourceReport')
    );
    this.perm.addPermission('reports_agent_role', () =>
      caps.check('canViewAgentRoleReport')
    );
    this.perm.addPermission('reports_company_leaderboard', () =>
      caps.check('canViewCompanyLeaderboardReport')
    );
    this.perm.addPermission('reports_goal_pacing', () =>
      caps.check('canViewGoalPacingReport')
    );

    // Connect nav — a React-surface deep-link into the FG Hub. FGR gates it on
    // canManageListing (staff + any staff role, no code) — NOT the connect_manager
    // code, which gates the manager tier *inside* the Connect page. The menu-bar is
    // *ngxPermissionsOnly-driven, so the capability is exposed under its own name for
    // the menu to reference directly (FGR-5891).
    this.perm.addPermission('canManageListing', () =>
      caps.check('canManageListing')
    );

    // "All States" filter on the Production Detail + Company Leaderboard reports —
    // CA/OM only (canFilterReportsByState deliberately omits teamlead/agent). Exposed
    // under its own name because the filter is a data-driven TableFilter consumed via
    // [ngxPermissionsOnly]="filter.permissions", not an *otCapability directive.
    this.perm.addPermission('canFilterReportsByState', () =>
      caps.check('canFilterReportsByState')
    );

    // ───────────────────────────────────────────────────────────────────────
    // Identity / generic / React-surface gates — NOT capabilities (playbook
    // principle #1: gate identity with role flags, gate actions with
    // capabilities). Updated to read the REAL canonical flags / permission codes
    // (the previous is_ot_admin / title-case-label reads matched fields the
    // backend no longer sends, so they silently evaluated to false).
    // ───────────────────────────────────────────────────────────────────────
    this.perm.addPermission('company_admin', () => !!roles()?.is_company_admin);
    this.perm.addPermission('team_lead', () => !!roles()?.is_teamlead);
    this.perm.addPermission('contacts_assigned_agent', () =>
      hasOversightRole(roles())
    );
    // Generic "any logged-in user" catch-all — unchanged.
    this.perm.addPermission('all_roles', () => !!this._state.value);
    // React-surface link — reads the real permission CODE (was a broken label /
    // retired-flag read).
    this.perm.addPermission('user_management_permission', () =>
      permissions().includes('user_management')
    );
  }
}
